import React, { useEffect, useState } from 'react';
import { marketplaceAPI } from '../services/api';

/**
 * What an R2R deal costs each side, shown before anyone commits to it.
 *
 * Ariv charges once, on the deal: 2% added to what the host pays and 6%
 * deducted from what the landlord receives. Neither figure is the rent the
 * two of them agreed between themselves, which is exactly why both have to
 * see this before they act rather than when the money moves.
 *
 * The numbers come from the server (/api/payments/r2r-deal/:id/breakdown),
 * which derives them from the same function that builds the Stripe charge and
 * settles the deal. Nothing here is calculated in the browser, so what a
 * person is shown and what they are actually charged cannot drift apart.
 *
 * `role` decides which side is displayed: 'host' sees what they pay,
 * 'landlord' sees what they receive.
 */
export default function R2RDealBreakdown({ offerId, role, token, onPay, paying }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!offerId || !token) return undefined;
    marketplaceAPI
      .getDealBreakdown(offerId, token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Could not load the fee breakdown.'); });
    return () => { cancelled = true; };
  }, [offerId, token]);

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!data) {
    return <p className="text-sm text-gray-500">Working out the figures…</p>;
  }

  const money = (n) => `£${Number(n).toFixed(2)}`;
  const isHost = role === 'host';
  const side = isHost ? data.host : data.landlord;
  const total = isHost ? side.totalToPay : side.totalToReceive;
  const alreadyPaid = data.paymentStatus === 'paid';

  return (
    <div className="rounded-lg border border-[#1F3A68]/20 bg-[#F4F2E9] p-4">
      <h4 className="text-sm font-semibold text-[#1F3A68] mb-3">
        {isHost ? 'What you pay to conclude this deal' : 'What you receive from this deal'}
      </h4>

      <dl className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <dt className="text-gray-700">Rent agreed with the {isHost ? 'landlord' : 'host'}</dt>
          <dd className="font-medium text-gray-900">{money(side.agreedRent)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-700">
            Ariv platform fee ({Math.round(side.platformFeeRate * 100)}%)
            {isHost ? ' — added' : ' — deducted'}
          </dt>
          <dd className={`font-medium ${isHost ? 'text-gray-900' : 'text-red-700'}`}>
            {isHost ? '+' : '−'}{money(side.platformFee)}
          </dd>
        </div>
        <div className="flex justify-between border-t border-[#1F3A68]/20 pt-2 mt-2">
          <dt className="font-semibold text-[#1F3A68]">
            {isHost ? 'Total to pay' : 'Total you receive'}
          </dt>
          <dd className="font-bold text-[#1F3A68] text-base">{money(total)}</dd>
        </div>
      </dl>

      <p className="mt-3 text-xs leading-relaxed text-gray-600">{side.explain}</p>

      {isHost && !alreadyPaid && onPay && (
        <button
          type="button"
          onClick={onPay}
          disabled={paying}
          className="mt-4 w-full rounded-lg bg-[#1F3A68] px-4 py-2.5 font-semibold text-white disabled:opacity-60"
        >
          {paying ? 'Opening payment…' : `Pay ${money(total)} and conclude the deal`}
        </button>
      )}

      {alreadyPaid && (
        <p className="mt-3 text-xs font-semibold text-green-700">
          Paid. Ariv takes nothing further from this tenancy.
        </p>
      )}
    </div>
  );
}
